// src/features/system/rbac/api/rbacApi.ts
// ————————————————————————————————————————————————————————————————
// BFF-backed RBAC API (uses /bff/rbac + cookie/CSRF via bff client)
// ————————————————————————————————————————————————————————————————
import { bff } from '../../../../lib/http/bff';

/* ── DTOs ───────────────────────────────────── */
export interface Ability {
  id_ability:  number;
  ability_key: string;
  description?: string | null;
}
export interface AbilityIn {
  ability_key: string;
  description?: string | null;
}

export interface Role {
  id_role:       number;
  role_key:      string;
  display_name?: string | null;
  description?:  string | null;
  /** optional FK to a service (for Role → Service tab) */
  service_id?:   number | null;
}
export interface RoleIn {
  role_key:      string;
  display_name?: string | null;
  description?:  string | null;
}

/* ── Base prefix — note: bff client already prefixes /bff ───────────── */
const BASE = '/rbac';

/* abilities ─────────────────────────────────── */
export const fetchAbilities = (): Promise<Ability[]> =>
  bff.get<Ability[]>(`${BASE}/abilities`);

export const createAbility = (payload: AbilityIn): Promise<Ability> =>
  bff.post<Ability, AbilityIn>(`${BASE}/ability`, payload);

export const deleteAbility = (ability_key: string): Promise<void> =>
  bff.del<void>(`${BASE}/ability/${encodeURIComponent(ability_key)}`);

/* roles ─────────────────────────────────────── */
export const fetchRoles = async (): Promise<Role[]> => {
  const raw = await bff.get<any[]>(`${BASE}/roles`);
  return (raw || []).map(r => ({
    id_role     : r.id_role,
    role_key    : r.role_key ?? r.role,
    display_name: r.display_name ?? r.role_name ?? null,
    description : r.description ?? null,
    service_id  : r.service_id ?? null,
  }));
};

export const createRole = (payload: RoleIn): Promise<Role> =>
  bff.post<Role, RoleIn>(`${BASE}/roles`, payload);

export const deleteRole = (role_key: string): Promise<void> =>
  bff.del<void>(`${BASE}/role/${encodeURIComponent(role_key)}`);

/* role → service (PUT) ──────────────────────── */
export async function setRoleService(id_role: number, id_service: number | null) {
  const body = id_service === null ? {} : { id_service };
  await bff.request<void>(`${BASE}/role/${id_role}/service`, {
    method : 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body   : JSON.stringify(body),
  });
}

/* role ↔ ability ────────────────────────────── */
export const fetchRoleAbilities = (role_key: string): Promise<string[]> =>
  bff.get<string[]>(`${BASE}/role/${encodeURIComponent(role_key)}/abilities`);

export const setRoleAbilities = (
  role_key:  string,
  abilities: string[],
): Promise<void> =>
  bff.post<void, { abilities: string[] }>(
    `${BASE}/role/${encodeURIComponent(role_key)}/abilities`,
    { abilities },
  );

/* user ↔ ability / role ─────────────────────── */
export const listUserAbilities = (id_user: number): Promise<string[]> =>
  bff.get<string[]>(`${BASE}/user/${id_user}/abilities`);

export const assignRoleToUser = (
  id_user:   number,
  role_key:  string,
  tenant_id?: number,
): Promise<void> =>
  bff.post<void, { role_key: string; tenant_id?: number }>(
    `${BASE}/user/${id_user}/role`,
    { role_key, tenant_id },
  );

export const removeRoleFromUser = (
  id_user:   number,
  role_key:  string,
  tenant_id?: number,
): Promise<void> =>
  bff.request<void>(`${BASE}/user/${id_user}/role`, {
    method : 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body   : JSON.stringify({ role_key, tenant_id }),
  });

/* user ↔ role ------------------------------------------------------------ */
export const fetchUserRoles = (uid: number): Promise<string[]> =>
  bff.get<string[]>(`${BASE}/user/${uid}/roles`);

/* (Legacy re-exports left as-is) */
export type { User } from '../../user/api/usersApi';
export { fetchUsers } from '../../user/api/usersApi';
